/**
  * @class meleCalendarSnapshots
  * @description
  *
  * # Purpose
  * Implements `<mele-calendar-snapshots>` custom web element for showing calendar image snapshots
  */

import msysListener from 'msysListener'
import meleElement from 'meleElement'

const PROPERTIES = _.extend({}, {
  listener_id: { type: 'string', description: 'desired listener_id for the streaming listener; if not given, automatic numeric ID will be assigned' },
  channel: { type: 'integer', description: 'video channel to be used for generating snapshots' },
  value: { type: 'string', description: 'current selected time in epoch seconds' },
  unit: { type: 'string', default: 'day', enum: ['year', 'month', 'day', 'hour', 'minute'], description: 'currently selected unit' },
  source: { type: 'string', description: 'source of streaming; empty string means our application, @ means peering, Bxxxxx: means remote machine', default: 'SERVER@msysStreamer' },
  eventValueChanged: { type: 'function', description: 'Called when user clicks on any snapshot element' },
}, meleElement._properties)

export default class meleCalendarSnapshots extends meleElement {
  init() {
    this.style.display = 'none'
    this.innerHTML = ''
    super.init()
    setTimeout(()=>this.refresh(), 10) // wait a bit to get listener_id attribute
  } // init

  channelChanged() {
    this.refresh()
  } // channelChanged

  unitChanged() {
    this.refresh()
  } // unitChanged

  sourceChanged() {
    this.refresh()
  } // sourceChanged

  valueChanged() {
    this.refresh()
  } // valueChanged

  async refresh() {
    const channel = this.channel
    const value = parseInt(this.value)
    const level = this.unit
    if(!(channel>0 && value>0 && level)) {
      this.innerHTML = ''
      this.style.display = 'none'
      return
    }

    const dt = new Date(value*1000)
    let start
    let end
    if(level==='second') {
      dt.setSeconds(0)
      start = Math.floor(dt.valueOf()/1000)
      end = start + 59
    }else if(level==='minute') {
      dt.setMinutes(0)
      start = Math.floor(dt.valueOf()/1000/60)
      end = start + 59
    }else if(level==='hour') {
      dt.setHours(0)
      start = Math.floor(dt.valueOf()/1000/60/60)
      end = start + 23
    }else if(level==='day') {
      const days = new Date(dt.getFullYear(), dt.getMonth() + 1, 0).getDate()
      dt.setDate(1)
      start = Math.floor(dt.valueOf()/1000/60/60/24)
      end = start + days - 1
    }else {
      this.innerHTML = ''
      this.style.display = 'none'
      return
    }

    if(this.last_start===start) return

    this.innerHTML = ''
    this.style.display = 'none'
    this.last_start = start

    if(!this.listener) this.listener = new msysListener(this.listener_id || 0)
    if(!this.listener.listener_id) {
      // creation of listener failed as there is already one running
      this.listener = this.listener._listeners[this.listener_id]?.ref
      if(!this.listener) return
    }

    try{
      const timeout = 100
      const { params: { list } } = await this.listener.request(this.source, 'mmGetKeyframeTimes', { channel, level, start, end, timeout }, { resolve: 'mmFrameTimes', timeout: 2000 })
      let something_appended = false
      for(const dt of list) {
        if(!this.isConnected || value!==parseInt(this.value) || level!==this.unit) break // calendar changed in the meantime
        try{
          const { params: { payload } } = await this.listener.request(this.source, 'mmGetJPEG', {
            channel,
            dt,
            around: 0,
            q: 2,
            max_width: 160,
            max_height: 120,
          }, 'mmJPEG')
          if(!this.isConnected || value!==parseInt(this.value) || level!==this.unit) break // calendar already changed
          const blob = new Blob([payload], { type: 'image/jpeg' })
          const url = URL.createObjectURL(blob)
          const img = await new Promise((resolve, reject) => {
            const img = new Image()
            img.onload = () => resolve(img)
            img.onerror = () => reject(new Error('Failed to load image'))
            img.src = url
          })
          URL.revokeObjectURL(url)
          const title = document.createElement('div')
          title.innerHTML = _.userdatetime(Math.floor(dt/1000))
          const el = document.createElement('snapshot')
          el.innerHTML = '<div class="left"></div><svg id="arrow.e" viewBox="-1 -1 32 32"><polyline points="8.5 0.5 21.5 15 8.5 29.5" fill="none" stroke="var(--fg2)" class="rounded"></polyline></svg>'
          const elLeft = el.find('.left')
          elLeft.append(title)
          elLeft.append(img)
          elLeft.onclick = this.clicked.bind(this, dt, false)
          const elSvg = el.find('svg')
          elSvg.onclick = this.clicked.bind(this, dt, true)
          if(!this.isConnected || value!==parseInt(this.value) || level!==this.unit) break // calendar already changed
          this.appendChild(el)
          if(!something_appended) {
            this.style.display = ''
            something_appended = true
          }
        }catch(e) {
          if(!this.isConnected || value!==parseInt(this.value) || level!==this.unit) return // calendar already changed
          this.error('failed to obtain snapshot image: '+e)
        }
      }
    }catch(e) {
      this.error(e)
    }
  } // refresh

  disconnectedCallback() {
    if(this.listener) {
      this.listener.mmExit(null, { delete: true })
      delete this.listener
    }
  } // disconnectedCallback

  clicked(usec, seek) {
    const old = this.value
    const value = Math.floor(usec/1_000_000)
    if(seek) {
      const elModal = this.closest('mele-modal')
      if(elModal?.performSeek) return elModal.performSeek(usec)
    }
    this.value = value
    if(this.eventValueChanged) this.eventValueChanged(this, value, old, true)
  } // clickedImage
} // class meleCalendarSnapshots

meleCalendarSnapshots._properties = PROPERTIES
meleCalendarSnapshots.observedAttributes = _.keys(PROPERTIES)
window.customElements.define('mele-calendar-snapshots', meleCalendarSnapshots)
